import map from 'lodash/map'
import mapValues from 'lodash/mapValues'
import size from 'lodash/size'
import { useQueryStates } from 'nuqs'
import React, { createContext, FC, useMemo } from 'react'
import useSWR from 'swr'

import { LookTile, ProductListBlockFragmentFragment } from '@Generated/graphql'
import {
  ProductSearchContext,
  ProductSearchContextType,
  ProductSearchStaticContext,
} from '@Globals/Algolia/Context'
import { algoliaFetcher } from '@Globals/Algolia/Fetcher'
import { algoliaLookHitToLookTile, buildLooksOptions } from '@Globals/Algolia/Looks/Shared'
import { computeFacetDynamicConfig, countAppliedFilters, createQueryStatesKeys, parseValidQueryStates } from '@Globals/Algolia/Shared'
import { AlgoliaLookHit, ProductSorting } from '@Globals/Algolia/Types'
import { useLocale } from '@Globals/Locale'
import { useTranslation } from '@Globals/Translations'

export interface LookSearchContextType {
  looks: LookTile[]
}

export const LookSearchContext = createContext<LookSearchContextType>({ looks: [] })

export interface LookSearchProviderProps {
  children?: React.ReactNode
  facetConfig: ProductListBlockFragmentFragment['facetConfig']
}

// LookSearchProvider fills the same two contexts as ProductSearchProvider so
// the shared filter and pagination components work unchanged, plus
// LookSearchContext with the tiles mapped straight from the Algolia hits.
export const LookSearchProvider: FC<LookSearchProviderProps> = ({ children, facetConfig: facetConfigInput }) => {
  const locale = useLocale()
  const { t } = useTranslation()

  // The ambassador facet is content-driven; its header is app chrome and
  // ships without a name from graph-api.
  const facetConfig = useMemo(() => map(facetConfigInput, facet => (facet.key === 'ambassadorHandle' && !facet.name
    ? { ...facet, name: { __typename: 'Wordbook' as const, defaults: t('common:looks_filter_ambassador', 'Ambassador') } }
    : facet)), [facetConfigInput, t])

  const queryStatesKeysMap = useMemo(() => createQueryStatesKeys(facetConfig), [facetConfig])
  const [queryStates, setQueryStates] = useQueryStates(queryStatesKeysMap)

  const queryStatesCount = useMemo(() => countAppliedFilters(queryStates), [queryStates])

  const options = useMemo(() => {
    const parsed = parseValidQueryStates(queryStates, facetConfig, {})

    // Only facetFilters and page carry over: looks have no sorting or ranges,
    // and the SSR options must serialize identically for SWR hydration.
    return buildLooksOptions({
      facetFilters: parsed.facetFilters,
      locale,
      page: parsed.page,
    })
  }, [facetConfig, locale, queryStates])

  const swr = useSWR(
    options,
    algoliaFetcher,
    {
      keepPreviousData: true,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  const facetDynamicConfig = useMemo(
    () => computeFacetDynamicConfig(
      facetConfig,
      options.facetFilters || [],
      options.numericFilters || {},
      swr?.data?.facets,
      swr?.data?.facetsStats,
    ),
    [
      facetConfig,
      options.facetFilters,
      options.numericFilters,
      swr?.data?.facets,
      swr?.data?.facetsStats,
    ],
  )

  const looks = useMemo(
    () => map((swr.data?.hits || []) as unknown as AlgoliaLookHit[], algoliaLookHitToLookTile),
    [swr.data?.hits],
  )

  const valueStatic = useMemo(() => ({
    facetConfig,
    initialFacetStats: {},
    injections: [],
    usePagination: true,
  }), [facetConfig])

  const value = useMemo(
    (): ProductSearchContextType => ({
      count: queryStatesCount,
      currentHits: swr.data?.totalHits || 0,
      objectIDs: swr.data?.objectIDs ?? [],
      facetDynamicConfig,
      guidedFilters: null,
      hasActiveFilter: size(options.facetFilters) !== 0,
      hits: {},
      hitsArray: [],
      numericFilters: {},
      page: options.page,
      pages: swr.data?.pages || 0,
      sorting: ProductSorting.Default,
      title: {
        base: '',
        disabled: false,
      },

      resetFilters() {
        // q belongs to the page's product list block sharing the URL — a looks
        // reset must not clear its search text
        setQueryStates(prev => ({
          ...mapValues(prev, () => null),
          q: prev.q,
          sorting: prev.sorting,
        }))
      },
    }),
    [
      facetDynamicConfig,
      options.facetFilters,
      options.page,
      queryStatesCount,
      setQueryStates,
      swr.data?.objectIDs,
      swr.data?.pages,
      swr.data?.totalHits,
    ],
  )

  const lookValue = useMemo(() => ({ looks }), [looks])

  return (
    <ProductSearchStaticContext.Provider value={valueStatic}>
      <ProductSearchContext.Provider value={value}>
        <LookSearchContext.Provider value={lookValue}>
          {children}
        </LookSearchContext.Provider>
      </ProductSearchContext.Provider>
    </ProductSearchStaticContext.Provider>
  )
}
